import React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { useSelector } from "react-redux"
import { Bars4Icon, XMarkIcon } from "@heroicons/react/24/outline" // Hamburger and close icons
import {
	HomeIcon,
	CalendarIcon,
	CogIcon,
	BellIcon,
	LifebuoyIcon,
	ClockIcon,
	ChartBarIcon,
	UserGroupIcon,
	SparklesIcon
} from "@heroicons/react/24/outline"
import { IoBarChartOutline } from "react-icons/io5"
import { IoAnalytics } from "react-icons/io5"
import Profile from "./Authentication/Profile"
import CustomSearchBox from "./CustomSearchBox"
import algoliasearch from "algoliasearch/lite"
import { InstantSearch, Hits, Highlight, connectStateResults } from "react-instantsearch-dom"
import posthog from "posthog-js"
import { useDispatch } from "react-redux"
import { setIsEditing } from "../features/schedule/scheduleSlice"
import useFetchNotifications from "../hooks/useFetchNotifications"
import { usePermissionsList } from "../hooks/queries/usePermissions"

const searchClient = algoliasearch("HWIR06EPUT", "bc157a3b5962cc2d21a132ccf0b6f202")

function classNames(...classes) {
	return classes.filter(Boolean).join(" ")
}

const Sidebar = ({ className, renderedHomeComponent, sidebarOpen, setSidebarOpen }) => {
	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { user } = useAuth0()
	const currentPath = location.pathname
	const { isAuthenticated } = useAuth0()
	const permissions = usePermissionsList()
	const isLocked = useSelector(state => state.schedule.isLocked)

	const { notificationsData } = useFetchNotifications(true)

	// Extract the user's unique identifier
	const userId = user?.sub
	// Filter notifications for the current user
	const userNotifications = notificationsData?.filter(n => n.person_id === userId)
	const unreadCount = userNotifications ? userNotifications.filter(n => n.active).length : 0

	const handleNavigation = (path, label) => {
		// Capture the button press event
		posthog.capture("button_pressed", {
			button_name: label,
			destination_path: path
		})

		dispatch(setIsEditing(false))

		navigate(path)
	}

	const handleResultClick = url => {
		navigate(url)
	}

	const Hit = ({ hit }) => (
		<div
			className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
			onClick={() => handleResultClick(hit.url)}
		>
			<h4 className="text-sm font-semibold text-gray-900">
				<Highlight attribute="title" hit={hit} />
			</h4>
			<p className="text-xs text-gray-700">
				<Highlight attribute="content" hit={hit} />
			</p>
		</div>
	)

	const Results = connectStateResults(({ searchState, searchResults, children }) =>
		searchState && searchState.query ? (
			searchResults && searchResults.nbHits !== 0 ? (
				children
			) : (
				<div className="px-4 py-2">No results found.</div>
			)
		) : null
	)

	const navigation = [
		{ name: "Home", href: "/home", icon: HomeIcon, permission: null },
		{
			name: "Daily Huddle",
			href: "/analyze-schedule",
			icon: IoBarChartOutline,
			permission: "read:daily-huddle"
		},
		{
			name: "IC Tracker",
			href: "/ic-tracker",
			icon: CalendarIcon,
			permission: "read:ic-tracker"
		},
		{
			name: "Future Huddle",
			href: "/future-huddle",
			icon: IoAnalytics,
			permission: "read:future-huddle"
		},
		{
			name: "Nurse View",
			href: "/nurse-view",
			icon: UserGroupIcon,
			permission: "read:schedule-personal"
		},
		{
			name: "Operational Reports",
			href: "/executive-reports",
			icon: ChartBarIcon,
			permission: "read:kpi-dashboard"
		},
		// { name: 'Nursing Leadership View', href: '/nursing-leadership-view', icon: ChartBarIcon, permission: 'read:kpi-dashboard' },
		{
			name: "Schedule Templates",
			href: "/schedule-templates",
			icon: ClockIcon,
			permission: "read:templates"
		}
	]

	const additionalLinks = [
		{
			name: "Notifications",
			href: "/notifications",
			icon: BellIcon,
			permission: "read:notifications"
		},
		{ name: "Support", href: "/support", icon: LifebuoyIcon, permission: "read:support" },
		{
			name: "What's New",
			href: "/whats-new",
			icon: SparklesIcon,
			permission: null
		},
		{ name: "Settings", href: "/settings", icon: CogIcon, permission: "read:settings" }
	]

	const filterNavigation = () => {
		if (!permissions) {
			return []
		}

		let filteredLinks = navigation.filter(item => {
			// If this is the IC Tracker item and user has read:schedules permission
			if (item.name === renderedHomeComponent) {
				return false
			}

			if (item.name === "IC Tracker" && !isLocked) {
				return false
			}

			// For all other items, use the normal permission check
			return !item.permission || permissions.includes(item.permission)
		})

		return filteredLinks
	}

	const filterAdditionalLinks = () => {
		if (!permissions) {
			return []
		}
		return additionalLinks.filter(
			item => !item.permission || permissions.includes(item.permission)
		)
	}

	const filteredNavigation = filterNavigation()
	const filteredAdditionalLinks = filterAdditionalLinks()

	const handleCloseSidebar = () => {
		setSidebarOpen(false) // Close the sidebar
	}

	return (
		<>
			{/* Hamburger Menu Button (top left corner for small screens) */}
			<div className="fixed top-0 left-0 p-4 z-[100]">
				<button
					className="text-gray-500 hover:text-gray-700 focus:outline-none"
					onClick={() => setSidebarOpen(!sidebarOpen)}
				>
					{sidebarOpen ? (
						<XMarkIcon className="h-6 w-6" />
					) : (
						<Bars4Icon className="h-6 w-6" />
					)}
				</button>
			</div>

			{/* Sidebar - Always visible and collapsible */}
			<div
				className={`fixed inset-y-0 left-0 z-[100] flex w-64 bg-customBg shadow-lg transition-transform duration-300 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
			>
				<div className="flex flex-col h-full overflow-y-auto px-4 py-4">
					<div className="flex h-16 items-center justify-between space-x-2">
						<div className="flex items-center space-x-[6px]">
							<img
								className="h-6 w-auto"
								src="/images/oncare_logo.svg"
								alt="Oncare"
							/>
							<span className="font-bold text-xl">Oncare</span>
							<img className="h-6 w-auto" src="/images/ai.svg" alt="AI" />
						</div>
						{/* Close Button for all screen sizes moved here */}
						<button
							className="text-gray-500 hover:text-gray-700 focus:outline-none"
							onClick={handleCloseSidebar}
						>
							<XMarkIcon className="h-6 w-6" />
						</button>
					</div>

					<div className="mt-4">
						<InstantSearch searchClient={searchClient} indexName="oncore_ai">
							<CustomSearchBox />
							<div className="mt-1 bg-white rounded-md shadow-lg z-[100]">
								<Results>
									<Hits hitComponent={Hit} />
								</Results>
							</div>
						</InstantSearch>
					</div>
					<nav className="mt-4 flex-1">
						<ul className="space-y-1">
							{filteredNavigation.map(item => (
								<li key={item.name}>
									<button
										onClick={() => handleNavigation(item.href, item.name)}
										className={classNames(
											currentPath === item.href
												? "bg-customBlue text-white shadow-2xl"
												: "text-gray-700 hover:bg-gray-50 hover:text-customBlue",
											"group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left"
										)}
									>
										<item.icon
											className={classNames(
												currentPath === item.href
													? "text-white"
													: "text-gray-400 group-hover:text-customBlue",
												"h-6 w-6"
											)}
											aria-hidden="true"
										/>
										<span className="flex-1">{item.name}</span>
									</button>
								</li>
							))}
						</ul>
					</nav>

					<nav className="mt-auto">
						<ul className="space-y-1">
							{filteredAdditionalLinks.map(item => (
								<li key={item.name}>
									<button
										onClick={() => handleNavigation(item.href)}
										className={classNames(
											currentPath === item.href
												? "bg-customBlue text-white shadow-2xl"
												: "text-gray-700 hover:bg-gray-50 hover:text-customBlue",
											"group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold w-full text-left"
										)}
									>
										<item.icon
											className={classNames(
												currentPath === item.href
													? "text-white"
													: "text-gray-400 group-hover:text-customBlue",
												"h-6 w-6"
											)}
											aria-hidden="true"
										/>
										<span className="flex-1">{item.name}</span>
										{item.name === "Notifications" && unreadCount > 0 && (
											<span className="bg-red-600 text-white rounded-md px-2 py-0.5 text-xs font-bold">
												{unreadCount}
											</span>
										)}
									</button>
								</li>
							))}
							{isAuthenticated && (
								<li className="relative py-4">
									<Profile />
								</li>
							)}
						</ul>
					</nav>
				</div>
			</div>
		</>
	)
}

export default Sidebar
